import { useMemo } from "react";
import classNames from "classnames";
import { useWorkspaceAccounts } from "@api/account.api";
import {
    isScheduleHoldingAccountType,
    isScheduleRecognitionAccountType,
} from "@common/constants/domains/accounting/scheduleAccountFilters";
import { ScheduleTemplateResponse } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { RevenueSubMode } from "@common/types/domains/accounting/schedule";
import { MultiEntityAccountCombobox } from "@components/accounts/MultiEntityAccountCombobox";
import { VStack } from "@design-system/VStack/VStack";
import { WorkspaceAccountWithEntity } from "../scheduleFormHelpers";
import { DrawerInlineRow } from "./DrawerInlineRow";
import { ScheduleDraft } from "./scheduleDraft";
import { ScheduleManualLabeledField } from "./ScheduleManualLabeledField";
import { ScheduleManualLayout } from "./scheduleManualLayout";
import drawerStyles from "../ScheduleDrawer.module.scss";

interface AccountFieldProps {
    layout: ScheduleManualLayout;
    label: string;
    accounts: WorkspaceAccountWithEntity[];
    accountId: string | null;
    entityId: number | null;
    isLoading: boolean;
    onChange: (accountId: string) => void;
    portalContainer: HTMLElement | undefined;
}

const AccountField = ({
    layout,
    label,
    accounts,
    accountId,
    entityId,
    isLoading,
    onChange,
    portalContainer,
}: AccountFieldProps) => {
    const picker = (
        <MultiEntityAccountCombobox
            selectedAccountName={
                accounts.find((account) => account.id === accountId)?.name
            }
            selectedEntityIds={entityId == null ? [] : [entityId]}
            accounts={accounts}
            accountsLoading={isLoading}
            coverage="all"
            size="md"
            clearable={false}
            container={portalContainer}
            onChange={(account) => {
                const constituent = account?.constituents.find(
                    (candidate) => candidate.entityId === entityId,
                );
                if (constituent) {
                    onChange(constituent.id);
                }
            }}
        />
    );
    if (layout === "drawer") {
        return <DrawerInlineRow label={label}>{picker}</DrawerInlineRow>;
    }
    return (
        <ScheduleManualLabeledField label={label}>
            {picker}
        </ScheduleManualLabeledField>
    );
};

interface ScheduleManualAccountsSectionProps {
    template: ScheduleTemplateResponse;
    entityId: number | null;
    draft: ScheduleDraft;
    onDraftChange: (draft: ScheduleDraft) => void;
    /** False once the schedule can no longer change. */
    isEditable: boolean;
    /** False on a change: re-posting never re-codes the source's deferred account. */
    isHoldingAccountEditable: boolean;
    layout: ScheduleManualLayout;
    portalContainer: HTMLElement | undefined;
}

export const ScheduleManualAccountsSection = ({
    template,
    entityId,
    draft,
    onDraftChange,
    isEditable,
    isHoldingAccountEditable,
    layout,
    portalContainer,
}: ScheduleManualAccountsSectionProps) => {
    const workspaceAccounts = useWorkspaceAccounts();
    const revenueSubMode = template.revenueSubMode ?? RevenueSubMode.deferred;
    const { holdingAccounts, recognitionAccounts } = useMemo(() => {
        const entityAccounts = (workspaceAccounts.data ?? []).filter(
            (account) => account.entityId === entityId,
        );
        return {
            holdingAccounts: entityAccounts.filter((account) =>
                isHoldingAccountEditable
                    ? isScheduleHoldingAccountType({
                          accountType: account.type,
                          mode: {
                              type: template.type,
                              revenueSubMode,
                              expenseSubMode: template.expenseSubMode,
                          },
                      })
                    : account.id === draft.holdingAccountId,
            ),
            recognitionAccounts: entityAccounts.filter((account) =>
                isEditable
                    ? isScheduleRecognitionAccountType({
                          accountType: account.type,
                          scheduleType: template.type,
                      })
                    : account.id === draft.recognitionAccountId,
            ),
        };
    }, [
        workspaceAccounts.data,
        entityId,
        isEditable,
        isHoldingAccountEditable,
        revenueSubMode,
        template.type,
        template.expenseSubMode,
        draft.holdingAccountId,
        draft.recognitionAccountId,
    ]);

    return (
        <VStack
            gap={12}
            className={
                layout === "drawer"
                    ? classNames(drawerStyles.card, drawerStyles.cardStack)
                    : undefined
            }
        >
            <AccountField
                layout={layout}
                label="Holding GL account"
                accounts={holdingAccounts}
                accountId={draft.holdingAccountId}
                entityId={entityId}
                isLoading={workspaceAccounts.isLoading}
                onChange={(holdingAccountId) =>
                    onDraftChange({ ...draft, holdingAccountId })
                }
                portalContainer={portalContainer}
            />
            <AccountField
                layout={layout}
                label="Recognition GL account"
                accounts={recognitionAccounts}
                accountId={draft.recognitionAccountId}
                entityId={entityId}
                isLoading={workspaceAccounts.isLoading}
                onChange={(recognitionAccountId) =>
                    onDraftChange({ ...draft, recognitionAccountId })
                }
                portalContainer={portalContainer}
            />
        </VStack>
    );
};
